import { ApiPlan } from 'features/subscription/components/subscription-plan';
import { SubscriptionDetails } from 'state/store/subscription/subscriptionStore';

export type SubscriptionTabView = {
  planName: string;
  planId: string;
  amountLabel: string;
  billingCycle: string;
  statusLabel: string;
  nextRenewalLabel: string;
  canUpgrade: boolean;
  canDowngrade: boolean;
  hasSubscription: boolean;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatCurrency = (minorAmount: number, currency = 'USD'): string => {
  const amount = minorAmount / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

const formatUtcDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
};

const isSamePlan = (plan: ApiPlan, details: SubscriptionDetails): boolean =>
  details.planCode?.toLowerCase() === plan.code?.toLowerCase() ||
  details.planCode === plan.planId ||
  details.planName?.toLowerCase() === plan.displayName?.toLowerCase();

const matchPlanPrice = (
  plan: ApiPlan | undefined,
  details: SubscriptionDetails
): ApiPlan['prices'][number] | undefined => {
  if (!plan?.prices.length) return undefined;
  if (details.priceId) {
    const byPriceId = plan.prices.find((item) => item.priceId === details.priceId);
    if (byPriceId) return byPriceId;
  }
  if (details.interval) {
    const byInterval = plan.prices.find(
      (item) => item.interval.toLowerCase() === details.interval?.toLowerCase()
    );
    if (byInterval) return byInterval;
  }
  return plan.prices.find((item) => item.interval.toLowerCase() === 'month') ?? plan.prices[0];
};

const planAmount = (plan: ApiPlan): number => {
  const price =
    plan.prices.find((item) => item.interval.toLowerCase() === 'month') ?? plan.prices[0];
  return price?.unitAmountMinor ?? 0;
};

export const buildSubscriptionTabView = ({
  subscriptionDetails,
  plans,
}: {
  subscriptionDetails: SubscriptionDetails | null;
  plans: ApiPlan[];
}): SubscriptionTabView => {
  if (!subscriptionDetails?.subscriptionId) {
    return {
      planName: 'No plan',
      planId: '',
      amountLabel: '-',
      billingCycle: '-',
      statusLabel: 'Inactive',
      nextRenewalLabel: '',
      canUpgrade: plans.length > 0,
      canDowngrade: false,
      hasSubscription: false,
    };
  }
  const currentPlan = plans.find((plan) => isSamePlan(plan, subscriptionDetails));
  const price = matchPlanPrice(currentPlan, subscriptionDetails);

  const currentAmount = currentPlan ? planAmount(currentPlan) : 0;
  const isCanceling = Boolean(subscriptionDetails.cancelAtPeriodEnd);
  const renewalIso = subscriptionDetails.nextPaymentAtUtc || subscriptionDetails.currentPeriodEnd;

  return {
    planName: subscriptionDetails.planName,
    planId: currentPlan?.planId ?? subscriptionDetails.planCode,
    amountLabel: price
      ? formatCurrency(price.unitAmountMinor, price.currencyCode)
      : '-',
    billingCycle: price?.interval ?? '-',
    statusLabel: subscriptionDetails.status || 'Active',
    nextRenewalLabel: !isCanceling && renewalIso ? formatUtcDate(renewalIso) : '',
    canUpgrade: !isCanceling && plans.some((plan) => plan.prices.length > 0 && planAmount(plan) > currentAmount),
    canDowngrade: !isCanceling && plans.some((plan) => plan.prices.length > 0 && planAmount(plan) < currentAmount),
    hasSubscription: true,
  };
};
